import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { supabase } from '../supabaseClient.js';
import { MEMORY_CACHE } from '../services/GlobalCache.js';
import CacheService from '../services/CacheService.js';
// DashboardNavbar removed



const MIN_WITHDRAW = 50000; // minimal penarikan

// --- Custom Modal Component ---
const ConfirmationModal = ({ isOpen, onClose, config }) => {
  if (!isOpen || !config) return null;

  const { title, message, type, onConfirm, confirmText, cancelText } = config;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 transform transition-all scale-100">
        <div className="text-center">
          {type === 'success' && (
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 mb-4">
              <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          {type === 'error' && (
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 mb-4">
              <svg className="h-6 w-6 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          )}
          {type === 'confirm' && (
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 mb-4">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
          )}

          {type === 'warning' && (
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 mb-4">
              <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          )}

          <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
          <p className="text-sm text-slate-500 whitespace-pre-line mb-6">{message}</p>

          <div className="flex gap-3 justify-center">
            {type === 'confirm' && (
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                onClick={onClose}
              >
                {cancelText || 'Batal'}
              </button>
            )}
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-colors ${type === 'error' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              onClick={() => {
                if (onConfirm) onConfirm();
                onClose();
              }}
            >
              {confirmText || 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Toast Notification Component ---
const ToastNotification = ({ toast, onClose }) => {
  useEffect(() => {
    if (toast.isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000); // hilangkan otomatis setelah 3 detik
      return () => clearTimeout(timer);
    }
  }, [toast.isOpen, onClose]);

  if (!toast.isOpen) return null;

  return (
    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-[60] flex items-center gap-3 px-4 py-3 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 animate-in fade-in slide-in-from-top-4 ${toast.type === 'error' ? 'bg-rose-500/90 text-white' : 'bg-slate-800/90 text-white'
      }`}>
      {toast.type === 'success' && (
        <svg className="h-5 w-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      )}
      {toast.type === 'error' && (
        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )}
      <p className="text-sm font-medium">{toast.message}</p>
    </div>
  );
};

const AffiliatorPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setPageTitle, profile: ctxProfile } = useOutletContext();

  useEffect(() => {
    setPageTitle(t('affiliator.pageTitle'));
  }, [setPageTitle, t]);

  const formatRupiah = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const [user, setUser] = useState(null);

  // Init profile from cache to prevent flickering
  const [profile, setProfile] = useState(() => ctxProfile || CacheService.get('user_profile') || null);

  const [userFeatures, setUserFeatures] = useState([]);
  const [sidebarItemsDb, setSidebarItemsDb] = useState([]);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    totalReferrals: 0,
    totalCommission: 0,
    pendingCommission: 0,
  });

  const [voucher, setVoucher] = useState(null);
  const [generatingVoucher, setGeneratingVoucher] = useState(false);

  const [activeTab, setActiveTab] = useState('ringkasan'); // 'ringkasan' | 'riwayat' | 'penarikan' | 'bank'

  const [packages, setPackages] = useState([]);
  const [commissionRate, setCommissionRate] = useState(10); // % komisi affiliator
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [transactionCount, setTransactionCount] = useState(10);

  // Data nyata
  const [commissionRows, setCommissionRows] = useState([]); // dari affiliate_commissions
  const [withdrawals, setWithdrawals] = useState([]); // dari affiliate_withdrawals

  // Form penarikan (tidak lagi diinput manual, tapi state dibiarkan supaya struktur tidak berubah)
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [submittingWithdraw, setSubmittingWithdraw] = useState(false);

  // Detail bank
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_number: '',
    account_holder: '',
  });
  const [savingBank, setSavingBank] = useState(false);

  // Accordion S&K: hanya satu terbuka, awalnya semua TERTUTUP
  const [activeAccordion, setActiveAccordion] = useState(null); // 'cara' | 'penggunaan' | 'penarikan' | null

  // --- Modal Logic & Toast ---
  const [modalState, setModalState] = useState({ isOpen: false, config: null });
  const [toastState, setToastState] = useState({ isOpen: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToastState({ isOpen: true, message, type });
  };

  const closeToast = () => {
    setToastState({ ...toastState, isOpen: false });
  };

  const showAlert = (title, message, type = 'info') => {
    // Jika type = success atau error, gunakan Toast (sesuai request user supaya tidak 'kebesaran')
    if (type === 'success' || type === 'error') {
      showToast(message, type);
      return;
    }
    setModalState({
      isOpen: true,
      config: { title, message, type }
    });
  };


  const showConfirm = (title, message, onConfirm) => {
    setModalState({
      isOpen: true,
      config: {
        title,
        message,
        type: 'confirm',
        onConfirm,
        confirmText: 'Ya, Lanjutkan',
        cancelText: 'Batal'
      }
    });
  };

  const closeModal = () => {
    setModalState({ ...modalState, isOpen: false });
  };

  const toggleAccordion = (key) => {
    setActiveAccordion((prev) => (prev === key ? null : key));
  };



  useEffect(() => {
    const fetchData = async () => {
      try {
        // CACHE-FIRST: Instant Load (Voucher & Stats)
        const cachedVoucher = MEMORY_CACHE['__GENERAL_VOUCHER__'] || CacheService.get('__GENERAL_VOUCHER__');
        const cachedStats = MEMORY_CACHE['__GENERAL_AFF_STATS__'] || CacheService.get('__GENERAL_AFF_STATS__');

        let shouldStopLoadingEarly = false;

        if (cachedVoucher) {
          setVoucher(cachedVoucher);
          if (cachedVoucher.commission_percent) setCommissionRate(Number(cachedVoucher.commission_percent));
          shouldStopLoadingEarly = true;
        }

        if (cachedStats && Array.isArray(cachedStats)) {
          setCommissionRows(cachedStats);
          // Calculate Stats from cached data
          let totalRef = 0;
          let totalComm = 0;
          let pendingComm = 0;
          cachedStats.forEach(c => {
            totalRef++;
            if (c.status === 'paid') totalComm += (Number(c.commission_amount) || 0);
            if (c.status === 'pending') pendingComm += (Number(c.commission_amount) || 0);
          });
          setStats({
            totalReferrals: totalRef,
            totalCommission: totalComm,
            pendingCommission: pendingComm,
          });
          shouldStopLoadingEarly = true;
        }

        if (shouldStopLoadingEarly) setLoading(false);
        else setLoading(true);

        // 1. 🔐 Cek user login (Blocking)
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          navigate('/login');
          return;
        }
        setUser(authUser);

        // 2. Fetch Profile (Required)
        const { data: profileData, error: profileError } = await supabase
          .from('profiles').select('*').eq('id', authUser.id).single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Profile error:', profileError);
        }
        if (!profileData) {
          navigate('/login');
          return;
        }
        setProfile(profileData);
        CacheService.set('user_profile', profileData, 60);

        // 3. Fetch Optional Data (with try-catch for missing tables)

        // User Features
        try {
          const { data } = await supabase
            .from('user_features').select('feature_code').eq('user_id', authUser.id);
          setUserFeatures(data ? data.map(r => r.feature_code) : []);
        } catch (e) { console.warn('user_features not available'); }

        // Vouchers
        let currentRate = 10;
        try {
          const { data: vouchersData } = await supabase
            .from('vouchers')
            .select('id, code, is_active, created_at, commission_percent')
            .eq('affiliator_id', authUser.id)
            .order('created_at', { ascending: true });

          if (vouchersData && vouchersData.length > 0) {
            setVoucher(vouchersData[0]);
            if (vouchersData[0].commission_percent != null) {
              currentRate = Number(vouchersData[0].commission_percent);
            }
          }
        } catch (e) { console.warn('vouchers not available'); }

        // App Settings (commission rate)
        try {
          const { data: settingData } = await supabase
            .from('app_settings').select('value').eq('key', 'affiliate_commission_percent').single();
          if (settingData?.value) currentRate = Number(settingData.value);
        } catch (e) { console.warn('app_settings not available'); }
        setCommissionRate(currentRate);

        // Credit Packages
        try {
          const { data: packagesData } = await supabase
            .from("credit_packages")
            .select("id,name,price,original_price,base_credits,populer,is_custom_button,button_text,button_url,custom_features")
            .eq("is_active", true)
            .order("price", { ascending: true });

          if (packagesData) {
            const valid = packagesData.filter(p => !p.name.toLowerCase().includes('debug aja') && Number(p.price) > 0);
            setPackages(valid);
            if (valid.length > 0) setSelectedPackageId(valid[0].id);
          }
        } catch (e) { console.warn('credit_packages not available'); }

        // Affiliate Commissions
        try {
          const { data: commissionsData } = await supabase
            .from('affiliate_commissions')
            .select('*')
            .eq('affiliator_id', authUser.id)
            .order('created_at', { ascending: false });

          const commRows = commissionsData || [];
          setCommissionRows(commRows);
          const uniqueReferrals = new Set(commRows.map(c => c.buyer_id).filter(Boolean)).size;
          setStats({
            totalReferrals: uniqueReferrals,
            totalCommission: commRows.reduce((acc, r) => acc + Number(r.commission_amount || 0), 0),
            pendingCommission: commRows.filter(r => r.status === 'pending').reduce((acc, r) => acc + Number(r.commission_amount || 0), 0)
          });
        } catch (e) { console.warn('affiliate_commissions not available'); }

        // Affiliate Withdrawals
        try {
          const { data: withdrawalsData } = await supabase
            .from('affiliate_withdrawals')
            .select('*')
            .eq('affiliator_id', authUser.id)
            .order('created_at', { ascending: false });
          setWithdrawals(withdrawalsData || []);
        } catch (e) { console.warn('affiliate_withdrawals not available'); }

        // Bank Info (from profiles, not separate table)
        if (profileData) {
          setBankForm({
            bank_name: profileData.bank_name || '',
            account_number: profileData.bank_account_number || '',
            account_holder: profileData.bank_account_holder || ''
          });
        }

      } catch (err) {
        console.error('Error fetching affiliator data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // --- EVENTS ---
  const handleGenerateVoucher = async () => {
    if (!user) return;

    showConfirm(
      t('affiliator.voucher.title'), // Title can be generic or specific
      t('affiliator.voucher.confirmGenerate'),
      async () => {
        try {
          setGeneratingVoucher(true);
          // generate random code: NEO + 5 random chars
          const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
          const newCode = `NEO-${randomStr}`;

          // Call Edge Function to create/renew voucher
          // (Atau insert langsung kalau RLS ngasih izin. Di sini kita coba panggil via supabase rpc atau insert langsung)
          // Kita coba insert langsung ke tabel 'vouchers'
          const { data, error } = await supabase
            .from('vouchers')
            .upsert(
              {
                affiliator_id: user.id,
                code: newCode,
                commission_percent: 10, // default
                is_active: true,
              },
              { onConflict: 'affiliator_id' }
            )
            .select()
            .single();

          if (error) throw error;

          setVoucher(data);
          showAlert('Sukses', t('affiliator.voucher.successGenerate', { code: data.code }), 'success');
        } catch (err) {
          console.error('Error generate voucher:', err);
          showAlert('Gagal', t('affiliator.voucher.failGenerate', { error: err.message }), 'error');
        } finally {
          setGeneratingVoucher(false);
        }
      }
    );
  };

  const handleCopyVoucher = () => {
    if (!voucher?.code) return;
    navigator.clipboard
      .writeText(voucher.code)
      .then(() => showAlert('Berhasil', t('affiliator.voucher.copied'), 'success'))
      .catch(() => showAlert('Gagal', t('affiliator.voucher.copyFail'), 'error'));
  };

  // 🔁 LOGIKA PENARIKAN BARU
  const handleSubmitWithdrawal = async (e) => {
    e.preventDefault();
    if (!canWithdrawNow || !user) return;

    // Konfirmasi
    showConfirm(
      t('affiliator.withdraw.title'),
      `Apakah Anda yakin ingin menarik semua saldo sebesar ${formatRupiah(withdrawableBalance)}?`,
      async () => {
        // Lanjutkan proses
        try {
          setSubmittingWithdraw(true);

          // 1. insert ke affiliate_withdrawals
          const { error: insertErr } = await supabase
            .from('affiliate_withdrawals')
            .insert({
              affiliator_id: user.id,
              amount: withdrawableBalance,
              status: 'pending', // atau processing
              note: 'Pengajuan dari dashboard',
            });

          if (insertErr) throw insertErr;

          // 2. update saldo affiliator -> 0
          const { error: updateErr } = await supabase
            .from('profiles')
            .update({ affiliate_commission_balance: 0 })
            .eq('id', user.id);

          if (updateErr) {
            showAlert('Perhatian', t('affiliator.withdraw.alertUpdateError'), 'error');
          }

          // Refresh data
          setProfile((prev) => ({ ...prev, affiliate_commission_balance: 0 }));

          // fetch ulang withdrawals
          const { data: wData } = await supabase
            .from('affiliate_withdrawals')
            .select('*')
            .eq('affiliator_id', user.id)
            .order('created_at', { ascending: false });
          setWithdrawals(wData || []);

          showAlert('Sukses', t('affiliator.withdraw.alertSuccess', { amount: formatRupiah(withdrawableBalance) }), 'success');
        } catch (err) {
          console.error('Withdraw error:', err);
          showAlert('Gagal', t('affiliator.withdraw.alertFail', { error: err.message }), 'error');
        } finally {
          setSubmittingWithdraw(false);
        }
      }
    );
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!isBankFormValid) {
      showAlert('Gagal', t('affiliator.bank.alertEmpty'), 'error');
      return;
    }

    try {
      setSavingBank(true);
      // Simpan ke profiles (kolom jsonb: bank_details atau kolom terpisah)
      // Asumsi di table profiles ada kolom: bank_name, bank_account_number, bank_account_holder
      // Atau kita simpan di satu kolom JSONB 'affiliate_info' ?
      // Cek skema di database.sql user tidak ada info detail.
      // Kita anggap ada kolom tsb, atau kita update feature ini nanti.
      // Untuk "mockup" agar jalan, kita simpan ke tabel profiles jika kolom ada,
      // kalau belum ada, kita anggap sukses saja (atau simpan di local state/metadata).

      // Update: kita gunakan 'metadata' kolom atau buat kolom baru.
      // Untuk amannya, kita update ke profiles dengan asumsi kolom sudah dibuatkan user request sebelumnya
      // atau kita simpan di 'affiliate_bank_details' (text/json).

      const updates = {
        bank_name: bankForm.bank_name,
        bank_account_number: bankForm.account_number,
        bank_account_holder: bankForm.account_holder,
      };

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      showAlert('Sukses', t('affiliator.bank.alertSuccess'), 'success');
    } catch (err) {
      console.error('Save bank error:', err);
      showAlert('Gagal', t('affiliator.bank.alertFail', { error: err.message }), 'error');
    } finally {
      setSavingBank(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-slate-50 p-6 flex flex-col space-y-8 animate-pulse">
        <div className="w-full h-12 bg-slate-200 rounded-lg"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-48 bg-slate-200 rounded-xl"></div>)}
        </div>
      </div>
    );
  }

  const displayName =
    profile?.username || user?.email?.split('@')[0] || 'User';
  const creditBalance = profile?.credit_balance ?? 0;
  const walletBalance = profile?.wallet_balance ?? 0;

  const withdrawableBalance = walletBalance;

  const hasBankInfo =
    bankForm.bank_name.trim() &&
    bankForm.account_number.trim() &&
    bankForm.account_holder.trim();

  const canWithdrawNow =
    withdrawableBalance >= MIN_WITHDRAW && hasBankInfo && !submittingWithdraw;

  // Kode kupon: hanya pakai kode voucher yang benar-benar ada
  const referralCode = voucher?.code || '';



  // Paket terpilih untuk simulasi
  const selectedPackage =
    packages.find((p) => p.id === selectedPackageId) || packages[0] || null;

  const packagePrice = selectedPackage ? Number(selectedPackage.price) : 0;

  // 💰 Logika:
  // - Pembeli dapat diskon 10%
  // - Affiliator dapat komisi X% (commissionRate) dari harga SETELAH diskon
  const discountRate = 10; // % diskon pembeli
  const buyerDiscount = (packagePrice * discountRate) / 100;
  const priceAfterDiscount = packagePrice - buyerDiscount;
  const perTxnCommission = (priceAfterDiscount * commissionRate) / 100;
  const totalCommissionEstimate = perTxnCommission * (transactionCount || 0);

  // Urutan: Home → Affiliator → Topup Kredit
  const defaultSidebarItems = [
    {
      key: 'home',
      label: t('sidebar.home'),
      route_path: '/dashboard',
      is_active: true,
      feature_code: null,
    },
    {
      key: 'affiliator',
      label: t('affiliator.pageTitle'),
      route_path: '/dashboard/affiliator',
      is_active: true,
      feature_code: null,
    },
    {
      key: 'topup',
      label: t('sidebar.topup'),
      route_path: '/dashboard/topup',
      is_active: true,
      feature_code: null,
    },
    {
      key: "history-generate",
      label: t('history.pageTitle'),
      route_path: "/dashboard/riwayat-generate",
      is_active: true,
      feature_code: null,
    },
  ];

  const allSidebarItems = [...defaultSidebarItems, ...sidebarItemsDb];

  // Tab definisi
  const tabs = [
    { key: 'ringkasan', label: t('affiliator.tabs.summary') },
    { key: 'riwayat', label: t('affiliator.tabs.history') },
    { key: 'penarikan', label: t('affiliator.tabs.withdrawal') },
    { key: 'bank', label: t('affiliator.tabs.bank') },
  ];

  return (
    <>
      <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="max-w-4xl mx-auto space-y-4 pt-4">

          {/* Voucher info + tombol Generate */}
          <div className="space-y-3">
            <div className="rounded-xl border border-blue-100 bg-white shadow-sm p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="space-y-0.5">
                <p className="text-[10px] uppercase tracking-wide text-blue-500 font-semibold">
                  {t('affiliator.voucher.title')}
                </p>
                {voucher ? (
                  <>
                    <div className="flex items-center gap-2">
                      <p className="text-xs sm:text-sm text-slate-800 font-semibold">
                        {t('affiliator.voucher.activeCode')}
                      </p>
                      <p className="text-sm sm:text-base font-mono font-bold text-blue-700">
                        {voucher.code}
                      </p>
                    </div>
                    <p className="text-[10px] text-slate-500 max-w-md leading-tight">
                      <Trans i18nKey="affiliator.voucher.benefit" values={{ rate: commissionRate }}>
                        <span className="font-semibold">10% discount</span>
                        <span className="font-semibold">{commissionRate}% commission</span>
                      </Trans>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-xs sm:text-sm text-slate-800 font-semibold">
                      {t('affiliator.voucher.noVoucher')}
                    </p>
                    <p className="text-[10px] text-slate-500 max-w-md leading-tight">
                      {t('affiliator.voucher.noVoucherDesc')}
                    </p>
                  </>
                )}
              </div>

              <div className="flex flex-col items-start md:items-end gap-1.5">
                {voucher && (
                  <>
                    <button
                      type="button"
                      onClick={handleCopyVoucher}
                      className="inline-flex items-center justify-center px-3 py-1.5 rounded-md bg-blue-600 text-white text-[10px] sm:text-xs font-semibold shadow-sm hover:bg-blue-700 transition-colors"
                    >
                      {t('affiliator.voucher.copy')}
                    </button>
                    <span className="inline-flex flex-wrap items-center px-2 py-0.5 rounded-full bg-emerald-50 text-[10px] font-semibold text-emerald-700 border border-emerald-100">
                      {t('affiliator.voucher.activeSince')}&nbsp;
                      {new Date(voucher.created_at).toLocaleDateString(
                        'id-ID',
                        {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        }
                      )}
                    </span>
                  </>
                )}
                {!voucher && (
                  <button
                    type="button"
                    onClick={handleGenerateVoucher}
                    disabled={generatingVoucher}
                    className="inline-flex items-center justify-center px-3 py-1.5 rounded-md bg-blue-600 text-white text-[10px] sm:text-xs font-semibold shadow-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {generatingVoucher ? (
                      <>
                        <span className="mr-2 inline-block h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('affiliator.voucher.generating')}
                      </>
                    ) : (
                      t('affiliator.voucher.generate')
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Tabs – scrollable di mobile */}
            <div className="w-full overflow-x-auto -mx-2 px-2 pb-1">
              <div className="inline-flex gap-2 min-w-max">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-semibold border whitespace-nowrap transition-colors ${activeTab === tab.key
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ================= TAB: RINGKASAN ================= */}
          {activeTab === 'ringkasan' && (
            <>
              {/* Info keuntungan kupon + statistik */}
              {voucher ? (
                <div className="grid gap-3 md:grid-cols-3">
                  {/* Keuntungan pembeli & affiliator */}
                  <div className="md:col-span-2 rounded-xl border border-blue-100 bg-white shadow-sm p-3">
                    <h2 className="text-sm font-semibold text-slate-900 mb-3">
                      {t('affiliator.summary.benefitTitle')}
                    </h2>

                    <div className="rounded-xl border border-dashed border-blue-200 bg-blue-50/40 p-4 space-y-4">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">
                          {t('affiliator.summary.codeTitle')}
                        </p>
                        <p className="text-lg font-mono font-bold text-blue-700 break-all">
                          {referralCode}
                        </p>
                      </div>

                      <ul className="mt-2 space-y-2 text-xs text-slate-600 list-disc list-inside">
                        <li>
                          <Trans i18nKey="affiliator.summary.buyerBenefit" values={{ rate: discountRate }}>
                            <span className="font-semibold">{discountRate}% discount from package price</span>
                          </Trans>
                        </li>
                        <li>
                          <Trans i18nKey="affiliator.summary.yourBenefit" values={{ rate: commissionRate }}>
                            <span className="font-semibold">{commissionRate}% commission from discounted price</span>
                          </Trans>
                        </li>
                        <li>
                          {t('affiliator.summary.moreTrans')}
                        </li>
                      </ul>

                      <p className="text-[11px] text-amber-600">
                        {t('affiliator.summary.tips')}
                      </p>
                    </div>
                  </div>

                  {/* Statistik singkat (REAL) */}
                  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 flex flex-col gap-2">
                    <h2 className="text-xs font-semibold text-slate-900">
                      {t('affiliator.summary.statsTitle')}
                    </h2>

                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">{t('affiliator.summary.totalReferral')}</span>
                        <span className="font-semibold text-slate-900">
                          {stats.totalReferrals}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">
                          {t('affiliator.summary.totalCommission')}
                        </span>
                        <span className="font-semibold text-emerald-600">
                          {formatRupiah(stats.totalCommission)}
                        </span>
                      </div>
                    </div>
                    <p className="mt-1 text-[10px] text-slate-400">
                      {t('affiliator.summary.statsDesc')}
                    </p>
                  </div>
                </div>
              ) : (
                // Jika BELUM punya voucher → info singkat saja
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
                  <h2 className="text-sm font-semibold text-slate-900 mb-2">
                    {t('affiliator.summary.codeTitle')}
                  </h2>
                  <p className="text-xs text-slate-500">
                    <Trans i18nKey="affiliator.summary.noVoucherStats">
                      <span className="font-semibold text-blue-600">generate voucher</span>
                    </Trans>
                  </p>
                </div>
              )}

              {/* Simulasi Pendapatan */}
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3">
                <h2 className="text-xs font-semibold text-slate-900 mb-1">
                  {t('affiliator.summary.simulationTitle')}
                </h2>
                <p className="text-[10px] text-slate-500 mb-3 max-w-2xl">
                  <Trans i18nKey="affiliator.summary.simulationDesc" values={{ discount: discountRate, commission: commissionRate }}>
                    <span className="font-semibold">buyer gets {discountRate}% discount and you get {commissionRate}% commission</span>
                  </Trans>
                </p>

                {packages.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    {t('affiliator.summary.noPackages')}
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-slate-700">
                          {t('affiliator.summary.packageLabel')}
                        </label>
                        <select
                          value={selectedPackageId || ''}
                          onChange={(e) =>
                            setSelectedPackageId(
                              e.target.value
                                ? Number(e.target.value)
                                : null
                            )
                          }
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {packages.map((pkg) => (
                            <option key={pkg.id} value={pkg.id}>
                              {pkg.name} ({formatRupiah(pkg.price)})
                            </option>
                          ))}
                        </select>
                        <p className="text-[11px] text-slate-400">
                          {t('affiliator.summary.packagePlaceholder')}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-slate-700">
                          {t('affiliator.summary.countLabel')}
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={transactionCount}
                          onChange={(e) =>
                            setTransactionCount(
                              Math.max(0, Number(e.target.value) || 0)
                            )
                          }
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="contoh: 10"
                        />
                        <p className="text-[11px] text-slate-400">
                          {t('affiliator.summary.countHint')}
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                        <p className="text-[11px] text-slate-500 mb-1">
                          {t('affiliator.summary.priceLabel')}
                        </p>
                        <p className="text-sm font-semibold text-slate-900">
                          {formatRupiah(packagePrice)}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-1">
                          {t('affiliator.summary.discountLabel')}{' '}
                          <span className="font-semibold">
                            {formatRupiah(buyerDiscount)} ({discountRate}%)
                          </span>
                        </p>
                      </div>

                      <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                        <p className="text-[11px] text-slate-500 mb-1">
                          {t('affiliator.summary.netPriceLabel')}
                        </p>
                        <p className="text-sm font-semibold text-slate-900">
                          {formatRupiah(priceAfterDiscount)}
                        </p>
                      </div>

                      <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                        <p className="text-[11px] text-slate-500 mb-1">
                          {t('affiliator.summary.estCommissionLabel')}
                        </p>
                        <p className="text-sm font-semibold text-blue-600">
                          {formatRupiah(totalCommissionEstimate)}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-1">
                          <Trans i18nKey="affiliator.summary.commissionPerTx" values={{ amount: formatRupiah(perTxnCommission), rate: commissionRate }}>
                            <span className="font-semibold text-emerald-600">{formatRupiah(perTxnCommission)}</span>
                          </Trans>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Syarat & Ketentuan - Accordion */}
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 space-y-2">
                <div>
                  <h2 className="text-xs font-semibold text-slate-900">
                    {t('affiliator.summary.termsTitle')}
                  </h2>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {t('affiliator.summary.termsDesc')}
                  </p>
                </div>

                <div className="space-y-3">
                  {/* Cara Mendapatkan Komisi (awal tertutup) */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleAccordion('cara')}
                      className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="text-left">
                        <p className="text-xs font-semibold text-slate-900 uppercase tracking-wide">
                          {t('affiliator.summary.accordion.howTo')}
                        </p>
                      </div>
                      <svg
                        className={`w-4 h-4 text-slate-500 transition-transform ${activeAccordion === 'cara' ? 'rotate-180' : ''
                          }`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {activeAccordion === 'cara' && (
                      <div className="px-4 py-3 sm:px-5 sm:py-4 bg-white border-t border-slate-200">
                        <ul className="text-xs text-slate-600 list-disc list-inside space-y-1">
                          {t('affiliator.summary.accordion.howToItems', { returnObjects: true, amount: formatRupiah(MIN_WITHDRAW) }).map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Penggunaan Kupon */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleAccordion('penggunaan')}
                      className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="text-left">
                        <p className="text-xs font-semibold text-slate-900 uppercase tracking-wide">
                          {t('affiliator.summary.accordion.usage')}
                        </p>
                      </div>
                      <svg
                        className={`w-4 h-4 text-slate-500 transition-transform ${activeAccordion === 'penggunaan' ? 'rotate-180' : ''
                          }`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {activeAccordion === 'penggunaan' && (
                      <div className="px-4 py-3 sm:px-5 sm:py-4 bg-white border-t border-slate-200">
                        <ul className="text-xs text-slate-600 list-disc list-inside space-y-1">
                          {t('affiliator.summary.accordion.usageItems', { returnObjects: true }).map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Persyaratan Penarikan Dana */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleAccordion('penarikan')}
                      className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="text-left">
                        <p className="text-xs font-semibold text-slate-900 uppercase tracking-wide">
                          {t('affiliator.summary.accordion.withdraw')}
                        </p>
                      </div>
                      <svg
                        className={`w-4 h-4 text-slate-500 transition-transform ${activeAccordion === 'penarikan' ? 'rotate-180' : ''
                          }`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {activeAccordion === 'penarikan' && (
                      <div className="px-4 py-3 sm:px-5 sm:py-4 bg-white border-t border-slate-200">
                        <ul className="text-xs text-slate-600 list-disc list-inside space-y-1">
                          {t('affiliator.summary.accordion.withdrawItems', { returnObjects: true, amount: formatRupiah(MIN_WITHDRAW) }).map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ================= TAB: RIWAYAT KOMISI ================= */}
          {activeTab === 'riwayat' && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h2 className="text-sm font-semibold text-slate-900">
                  {t('affiliator.history.title')}
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs sm:text-sm text-slate-600">
                  <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="px-3 sm:px-4 py-2">{t('affiliator.history.table.date')}</th>
                      <th className="px-3 sm:px-4 py-2">{t('affiliator.history.table.buyer')}</th>
                      <th className="px-3 sm:px-4 py-2">{t('affiliator.history.table.affiliator')}</th>
                      <th className="px-3 sm:px-4 py-2">{t('affiliator.history.table.code')}</th>
                      <th className="px-3 sm:px-4 py-2">
                        {t('affiliator.history.table.count')}
                      </th>
                      <th className="px-3 sm:px-4 py-2">{t('affiliator.history.table.discount')}</th>
                      <th className="px-3 sm:px-4 py-2 text-right">
                        {t('affiliator.history.table.commission')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissionRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 sm:px-4 py-6 text-center text-slate-400 text-xs"
                        >
                          {t('affiliator.history.empty')}
                        </td>
                      </tr>
                    ) : (
                      commissionRows.map((row) => (
                        <tr
                          key={row.id}
                          className="border-b border-slate-100 last:border-0"
                        >
                          <td className="px-3 sm:px-4 py-2 align-top">
                            {new Date(row.created_at).toLocaleString(
                              'id-ID',
                              {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              }
                            )}
                          </td>
                          <td className="px-3 sm:px-4 py-2 align-top">
                            {row.buyer_name || '-'}
                          </td>
                          <td className="px-3 sm:px-4 py-2 align-top">
                            {displayName}
                          </td>
                          <td className="px-3 sm:px-4 py-2 align-top">
                            {row.voucher_code || referralCode || '-'}
                          </td>
                          <td className="px-3 sm:px-4 py-2 align-top">
                            {formatRupiah(row.transaction_amount)}
                          </td>
                          <td className="px-3 sm:px-4 py-2 align-top">
                            {formatRupiah(row.discount_amount)}
                          </td>
                          <td className="px-3 sm:px-4 py-2 align-top text-right font-semibold text-emerald-600">
                            {formatRupiah(row.commission_amount)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ================= TAB: PENARIKAN ================= */}
          {activeTab === 'penarikan' && (
            <div className="space-y-3">
              {/* grid: form kiri, tabel kanan di layar besar */}
              <div className="grid gap-3 lg:grid-cols-3">
                {/* FORM PENARIKAN (warna & logika baru) */}
                <div className="rounded-xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-slate-50 shadow-sm p-3 lg:col-span-1">
                  <h2 className="text-sm font-semibold text-slate-900 mb-3">
                    {t('affiliator.withdraw.title')}
                  </h2>
                  <p className="text-xs text-slate-500 mb-4">
                    <Trans i18nKey="affiliator.withdraw.desc" values={{ amount: formatRupiah(MIN_WITHDRAW) }}>
                      <span className="font-semibold">entire balance</span>
                      <span className="font-semibold">{formatRupiah(MIN_WITHDRAW)}</span>
                    </Trans>
                  </p>

                  <div className="mb-4 rounded-xl bg-white/80 border border-slate-200 px-4 py-3">
                    <p className="text-[11px] text-slate-500 mb-1">
                      {t('affiliator.withdraw.balanceLabel')}
                    </p>
                    <p className="text-sm font-semibold text-emerald-600">
                      {formatRupiah(withdrawableBalance)}
                    </p>
                  </div>

                  <form className="space-y-4" onSubmit={handleSubmitWithdrawal}>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-slate-700">
                        {t('affiliator.withdraw.amountLabel')}
                      </label>
                      <div className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm flex items-center justify-between">
                        <span className="text-xs text-slate-400 mr-2">Rp</span>
                        <span className="text-sm font-semibold text-slate-900">
                          {withdrawableBalance.toLocaleString('id-ID')}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        {t('affiliator.withdraw.amountHint')}
                      </p>

                      {withdrawableBalance < MIN_WITHDRAW && (
                        <p className="text-[11px] text-rose-600 mt-1">
                          {t('affiliator.withdraw.lowBalance', { amount: formatRupiah(MIN_WITHDRAW) })}
                        </p>
                      )}

                      {!hasBankInfo && (
                        <p className="text-[11px] text-amber-600 mt-1">
                          <Trans i18nKey="affiliator.withdraw.noBank">
                            <button type="button" className="underline text-blue-600" onClick={() => setActiveTab('bank')}>Bank/E-wallet Details</button>
                          </Trans>
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={!canWithdrawNow}
                      className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-blue-600 text-xs sm:text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {submittingWithdraw ? t('affiliator.withdraw.submitting') : t('affiliator.withdraw.submit')}
                    </button>
                  </form>
                </div>

                {/* Riwayat Penarikan (real) */}
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 lg:col-span-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                    <h2 className="text-sm font-semibold text-slate-900">
                      {t('affiliator.withdraw.historyTitle')}
                    </h2>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs sm:text-sm text-slate-600">
                      <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="px-3 sm:px-4 py-2">{t('affiliator.withdraw.table.date')}</th>
                          <th className="px-3 sm:px-4 py-2">{t('affiliator.withdraw.table.amount')}</th>
                          <th className="px-3 sm:px-4 py-2">{t('affiliator.withdraw.table.status')}</th>
                          <th className="px-3 sm:px-4 py-2">{t('affiliator.withdraw.table.note')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawals.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-3 sm:px-4 py-6 text-center text-slate-400 text-xs"
                            >
                              {t('affiliator.withdraw.empty')}
                            </td>
                          </tr>
                        ) : (
                          withdrawals.map((w) => (
                            <tr
                              key={w.id}
                              className="border-b border-slate-100 last:border-0"
                            >
                              <td className="px-3 sm:px-4 py-2 align-top">
                                {new Date(w.created_at).toLocaleString(
                                  'id-ID',
                                  {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  }
                                )}
                              </td>
                              <td className="px-3 sm:px-4 py-2 align-top">
                                {formatRupiah(w.amount)}
                              </td>
                              <td className="px-3 sm:px-4 py-2 align-top">
                                <span
                                  className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${w.status === 'success'
                                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                    : w.status === 'failed'
                                      ? 'bg-rose-100 text-rose-800 border border-rose-200'
                                      : 'bg-amber-100 text-amber-800 border border-amber-200'
                                    }`}
                                >
                                  {w.status}
                                </span>
                              </td>
                              <td className="px-3 sm:px-4 py-2 align-top">
                                {w.note || '-'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= TAB: DETAIL BANK ================= */}
          {activeTab === 'bank' && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">
                {t('affiliator.bank.title')}
              </h2>
              <p className="text-xs text-slate-500 mb-4">
                {t('affiliator.bank.desc')}
              </p>

              <form className="space-y-4" onSubmit={handleSaveBank}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-slate-700">
                      {t('affiliator.bank.bankName')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('affiliator.bank.bankPlaceholder')}
                      value={bankForm.bank_name}
                      onChange={(e) =>
                        setBankForm((prev) => ({
                          ...prev,
                          bank_name: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-slate-700">
                      {t('affiliator.bank.accountNumber')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('affiliator.bank.accountPlaceholder')}
                      value={bankForm.account_number}
                      onChange={(e) =>
                        setBankForm((prev) => ({
                          ...prev,
                          account_number: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="space-y-1 md:col-span-2">
                    <label className="block text-xs font-medium text-slate-700">
                      {t('affiliator.bank.accountHolder')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('affiliator.bank.holderPlaceholder')}
                      value={bankForm.account_holder}
                      onChange={(e) =>
                        setBankForm((prev) => ({
                          ...prev,
                          account_holder: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={savingBank}
                    className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-blue-600 text-xs sm:text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {savingBank
                      ? t('affiliator.bank.saving')
                      : t('affiliator.bank.save')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>

      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        config={modalState.config}
      />
      <ToastNotification
        toast={toastState}
        onClose={closeToast}
      />
    </>
  );
};

export default AffiliatorPage;
